import { nowUtc } from '@/lib/clock';
import { newUuid } from '@/lib/ids';
import { db as defaultDb, type GentleTaskDatabase } from './database';
import { SETTINGS_ID, WAITING_THRESHOLD_DEFAULT_DAYS, type Settings } from './settings.schema';
import type { BoardColumn, BoardColumnKind } from './board.schema';
import type { Classification } from './contact.schema';

/** data-model 4.2 的初始種子資料。 */
export const DEFAULT_CONTACT_TYPES = [
  '公司內部',
  '客戶',
  '廠商',
  '合作夥伴',
  '訪客',
  '私人',
  '其他',
] as const;

export const DEFAULT_RELATIONSHIPS = [
  '主管',
  '同事',
  '部屬',
  '客戶窗口',
  '廠商窗口',
  '主要聯絡人',
  '會議參與者',
  '其他',
] as const;

/** data-model 6.1 的初始欄位。最左側純由 order 決定，不必是代辦。 */
const DEFAULT_COLUMNS: ReadonlyArray<{ name: string; kind: BoardColumnKind; icon: string }> = [
  // 秘書最常被追的就是「這件事跟老闆講了沒」，所以它預設放在最左邊第一欄。
  { name: '提醒老闆', kind: 'normal', icon: 'MegaphoneSimple' },
  { name: '代辦', kind: 'normal', icon: 'Tray' },
  { name: '進行中', kind: 'normal', icon: 'PencilSimpleLine' },
  { name: '卡住', kind: 'normal', icon: 'Warning' },
  { name: '觀察', kind: 'normal', icon: 'Eye' },
  { name: '已完成', kind: 'completed_queue', icon: 'CheckCircle' },
];

function makeAudit(timestamp: string) {
  return { createdAt: timestamp, updatedAt: timestamp, revision: 1 };
}

/**
 * 產生一組預設欄位。播種與「匯入到空白工作台但備份裡沒有看板欄位」都用這一份，
 * 兩邊的欄位定義才不會各走各的。
 */
export function buildDefaultColumns(timestamp: string): BoardColumn[] {
  return DEFAULT_COLUMNS.map((column, index) => ({
    id: newUuid(),
    name: column.name,
    order: index,
    color: null,
    icon: column.icon,
    description: null,
    kind: column.kind,
    collapsed: false,
    archivedAt: null,
    ...makeAudit(timestamp),
  }));
}

function makeClassification(name: string, order: number, timestamp: string): Classification {
  return {
    id: newUuid(),
    name,
    order,
    color: null,
    disabledAt: null,
    ...makeAudit(timestamp),
  };
}

/**
 * 確保 settings 這一列存在。
 * **不播種任何欄位或分類**：清除資料與首次使用都必須進入 seededAt 為 null 的空白狀態，
 * 否則使用者匯入備份時會與種子欄位合併成兩倍欄位（data-model 12.4）。
 */
export async function ensureSettings(db: GentleTaskDatabase = defaultDb): Promise<Settings> {
  const existing = await db.settings.get(SETTINGS_ID);
  if (existing) return existing;

  const timestamp = nowUtc();
  const settings: Settings = {
    id: SETTINGS_ID,
    theme: 'system',
    defaultBoardColumnId: null,
    waitingThresholdDays: WAITING_THRESHOLD_DEFAULT_DAYS,
    browserNotificationsEnabled: false,
    recentStickers: [],
    lastDataMutationAt: null,
    seededAt: null,
    ...makeAudit(timestamp),
  };
  await db.settings.put(settings);
  return settings;
}

export async function isSeeded(db: GentleTaskDatabase = defaultDb): Promise<boolean> {
  const settings = await db.settings.get(SETTINGS_ID);
  return settings?.seededAt != null;
}

/**
 * 「建立全新的工作台」：建立預設欄位與分類，並寫入 seededAt。
 * 已播種時直接返回，避免重複建立。
 */
export async function seedWorkspace(db: GentleTaskDatabase = defaultDb): Promise<void> {
  await ensureSettings(db);
  if (await isSeeded(db)) return;

  const timestamp = nowUtc();
  const columns = buildDefaultColumns(timestamp);

  const contactTypes = DEFAULT_CONTACT_TYPES.map((name, index) =>
    makeClassification(name, index, timestamp),
  );
  const relationships = DEFAULT_RELATIONSHIPS.map((name, index) =>
    makeClassification(name, index, timestamp),
  );

  // 新任務的預設欄位＝最左側的一般欄位，也就是「提醒老闆」（spec 13.1）。
  // 使用者要換的話，設定頁的「新任務預設欄位」可以隨時改。
  const defaultColumn = columns.find((column) => column.kind === 'normal');

  await db.transaction(
    'rw',
    [db.boardColumns, db.contactTypes, db.relationships, db.settings],
    async () => {
      await db.boardColumns.bulkPut(columns);
      await db.contactTypes.bulkPut(contactTypes);
      await db.relationships.bulkPut(relationships);
      await db.settings.update(SETTINGS_ID, {
        seededAt: timestamp,
        defaultBoardColumnId: defaultColumn?.id ?? null,
        updatedAt: timestamp,
      });
    },
  );
}
